<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 hide-on-print">
      <SimplePageHeader title="Payroll" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="cloud_download"
            color="primary"
            text="Excel"
            class="hide-on-print"
            @click="handleExportToExcel"
            :block="true"
          />
          <ButtonPrint
            class="mt-3"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <PayrollReportFilters
        class="px-5 mb-sm-5 pb-8"
        @clientFilterChange="(newValue) => this.clientIdFilter = newValue"
      ></PayrollReportFilters>
    </div>
    <v-row
      v-if="payFrequencyId && clientPayrollSummariesDataValue.length > 0"
      class="hide-on-print"
    >
      <v-col cols="12" class="pt-sm-0">
        <v-card class="mt-3 mt-md-0">
          <v-card-text>
            <v-row>
              <v-col cols="12" class="px-4">
                <span class="headline">
                  <small>
                    Proration Needed
                    <div class="yellow-square"></div>
                  </small>
                </span>
                <span class="headline ml-5">
                  <small>
                    Total Pay Edited
                    <div class="blue-square"></div>
                  </small>
                </span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <ReportHeader
      reportTitle="Payroll Report"
      :reportCriteria="reportFilterCriteria"
    ></ReportHeader>
    <v-row>
      <v-col cols="12">
        <NoDataAvailableMessage
          v-if="payFrequencyId && clientPayrollSummariesDataValue.length === 0"
          message="No data for this time period"
        ></NoDataAvailableMessage>
        <PayrollReportTable
          v-if="payFrequencyId && clientPayrollSummariesDataValue.length > 0"
          :clientPayrollSummaries="clientPayrollSummariesDataValue"
          :staffPayrollSummaries="payrollReport.staffPayrollSummaries"
          @OneOffFeeListModalClose="getPayrollReportWithFilters"
          @editedTotalPay="getPayrollReportWithFilters"
        ></PayrollReportTable>
      </v-col>
    </v-row>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { ButtonPrint, BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import ReportHeader from '@/components/reports/ReportHeader'
import PayrollReportFilters from './PayrollReportFilters'
import PayrollReportTable from './PayrollReportTable'
import { SimplePageHeader } from '@/components/pageComponents'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions } from 'vuex'
import { formatCurrency, formatDate, getPayRateTypeLabel, getPayFrequencyLabel, formatDecimal } from '@/lib/display'
import excel from '@/lib/export/excel'
import { NoDataAvailableMessage } from '@/components/reports'

export default {
  components: {
    ButtonPrint,
    BottomSpacer,
    ReportHeader,
    PayrollReportFilters,
    PayrollReportTable,
    SimplePageHeader,
    ButtonCustomIcon,
    NoDataAvailableMessage
  },
  data () {
    return {
      payrollSummariesEmpty: [],
      clientIdFilter: null
    }
  },
  computed: {
    ...mapFields('payrollReport', ['payrollReport']),
    ...mapFields('payrollReportFilters', ['payFrequencyId', 'dateBegin', 'dateEnd', 'showDetails']),
    clientPayrollSummariesDataValue: {
      get () {
        if (this.payrollReport &&
          this.payrollReport.clientPayrollSummaries &&
          this.payrollReport.clientPayrollSummaries.length > 0) {
          if (this.clientIdFilter) {
            return this.payrollReport.clientPayrollSummaries.filter(row => row.clientId === this.clientIdFilter)
          } else {
            return this.payrollReport.clientPayrollSummaries
          }
        }
        return this.payrollSummariesEmpty
      }
    },
    staffPayrollSummaries: {
      get () {
        if (this.payrollReport &&
          this.payrollReport.staffPayrollSummaries &&
          this.payrollReport.staffPayrollSummaries.length > 0) {
          return this.payrollReport.staffPayrollSummaries
        } else {
          return this.payrollSummariesEmpty
        }
      }
    },
    reportFilterCriteria: {
      get () {
        return [
          { 'key': 'Pay Frequency', 'value': getPayFrequencyLabel(this.payFrequencyId) },
          { 'key': 'Period', 'value': formatDate(this.dateBegin) + ' - ' + formatDate(this.dateEnd) }
        ]
      }
    }
  },
  methods: {
    ...mapActions('payrollReport', ['getPayrollReportWithFilters', 'clearPayrollReport']),
    ...mapActions('payrollReportFilters', ['clearPayrollReportFilters']),
    handleExportToExcel () {
      let data = []
      if (this.clientPayrollSummariesDataValue.length > 0) {
        for (let client of this.clientPayrollSummariesDataValue) {
          for (let summary of client.payrollSummaries) {
            data.push({
              payFrequency: getPayFrequencyLabel(this.payFrequencyId),
              periodPaid: formatDate(this.dateBegin) + ' - ' + formatDate(this.dateEnd),
              consultant: summary.userFullName,
              client: summary.clientName,
              payRate: formatCurrency(summary.payRate),
              payRateType: getPayRateTypeLabel(summary.payRateTypeId),
              stHours: summary.stHours !== null ? formatDecimal(summary.stHours) : 'N/A',
              stPay: summary.stPay !== null ? formatCurrency(summary.stPay) : 'N/A',
              otHours: summary.otHours !== null ? formatDecimal(summary.otHours) : 'N/A',
              otPay: summary.otPay !== null ? formatCurrency(summary.otPay) : 'N/A',
              totalPaidHours: summary.totalPaidHours !== null ? parseFloat(summary.totalPaidHours).toFixed(2) : 'N/A',
              totalPay: formatCurrency(summary.totalPay)
            })
          }
        }
      }
      if (this.payrollReport &&
        this.payrollReport.staffPayrollSummaries &&
        this.payrollReport.staffPayrollSummaries.length > 0) {
        for (let summary of this.payrollReport.staffPayrollSummaries) {
          data.push({
            payFrequency: getPayFrequencyLabel(this.payFrequencyId),
            periodPaid: formatDate(this.dateBegin) + ' - ' + formatDate(this.dateEnd),
            consultant: summary.userFullName,
            client: summary.clientName,
            payRate: formatCurrency(summary.payRate),
            payRateType: getPayRateTypeLabel(summary.payRateTypeId),
            stHours: 'N/A',
            stPay: 'N/A',
            otHours: 'N/A',
            otPay: 'N/A',
            totalPaidHours: 'N/A',
            totalPay: formatCurrency(summary.totalPay)
          })
        }
      }
      if (data.length > 0) {
        let headers = []
        let columns = []
        if (!this.showDetails) {
          headers = ['Pay Frequency', 'Period Paid', 'Client', 'Consultant', 'Pay Rate', 'Pay Rate Type', 'Total Paid Hours', 'Total Pay']
          columns = ['payFrequency', 'periodPaid', 'client', 'consultant', 'payRate', 'payRateType', 'totalPaidHours', 'totalPay']
        } else {
          headers = ['Pay Frequency', 'Period Paid', 'Client', 'Consultant', 'Pay Rate', 'Pay Rate Type', 'ST', 'ST Pay', 'OT', 'OT Pay', 'Total Paid Hours', 'Total Pay']
          columns = ['payFrequency', 'periodPaid', 'client', 'consultant', 'payRate', 'payRateType', 'stHours', 'stPay', 'otHours', 'otPay', 'totalPaidHours', 'totalPay']
        }
        const params = {
          title: headers,
          key: columns,
          data: data,
          autoWidth: true,
          filename: `Payroll`
        }
        excel.exportArray2Excel(params)
      }
    }
  },
  created () {
    this.clearPayrollReportFilters()
    this.clearPayrollReport()
  }
}
</script>
